/**
 * BestsellerCurator — iter42 admin-only Crowd Favourites curation panel.
 *
 * The Homepage's "Crowd Favourites" strip is driven by an ordered list of
 * product_ids stored in settings.bestseller_product_ids.  This panel lets
 * the admin:
 *   - Add any active product from a category-filtered dropdown.
 *   - Remove a product from the list.
 *   - Reorder via ↑ / ↓ buttons.
 *
 * The change is purely local (lifted to parent via `onChange`).  Parent
 * is responsible for saving on the global Save bar.
 */
import { useMemo, useState } from "react";
import { ArrowUp, ArrowDown, Plus, Trash2, Sparkles, Wand2 } from "lucide-react";
import { imageUrl } from "../../lib/cloudinary";
import { BUNDLED_BESTSELLER_NAMES } from "../../lib/homeContent";

export default function BestsellerCurator({ productIds, allProducts, onChange }) {
  const [filter, setFilter] = useState("All");

  const byId = useMemo(() => {
    const m = new Map();
    (allProducts || []).forEach((p) => m.set(p.product_id, p));
    return m;
  }, [allProducts]);

  // Resolve the bundled-default bestseller names against the live product
  // list — used by the "Use current homepage defaults" prefill button so
  // admins can mirror exactly what's currently rendered on the homepage
  // (which is what they see before they curate anything explicit).
  const defaultIds = useMemo(() => {
    return BUNDLED_BESTSELLER_NAMES
      .map((name) => (allProducts || []).find((p) => p.name.toLowerCase() === name.toLowerCase()))
      .filter(Boolean)
      .map((p) => p.product_id);
  }, [allProducts]);

  const ordered = productIds.map((pid) => byId.get(pid)).filter(Boolean);
  const remaining = (allProducts || [])
    .filter((p) => !productIds.includes(p.product_id))
    .filter((p) => filter === "All" || p.category === filter);

  const categories = ["All", ...Array.from(new Set((allProducts || []).map((p) => p.category)))];

  const add = (pid) => onChange([...productIds, pid]);
  const remove = (pid) => onChange(productIds.filter((x) => x !== pid));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= productIds.length) return;
    const arr = [...productIds];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const useDefaults = () => onChange(defaultIds);

  return (
    <section className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6" data-testid="admin-bestsellers">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="font-serif text-2xl text-brand-dark flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-brand-chili" strokeWidth={1.6} />
          Crowd Favourites
        </h2>
        {defaultIds.length > 0 && (
          <button
            type="button"
            onClick={useDefaults}
            className="text-xs text-brand-chili font-medium inline-flex items-center gap-1 hover:underline"
            data-testid="bestseller-use-defaults"
          >
            <Wand2 className="w-3.5 h-3.5" strokeWidth={1.7} />
            Use current homepage defaults
          </button>
        )}
      </div>
      <p className="text-xs text-brand-muted mb-5">
        Pick the products shown in the homepage&apos;s &ldquo;Crowd Favourites&rdquo; section.
        Order here is the order shown on the homepage. Removed products vanish instantly after Save.
        Leave the list empty to fall back to the bundled defaults.
      </p>

      {/* Selected list */}
      {ordered.length === 0 ? (
        <div className="text-xs text-brand-muted bg-brand-cream/60 border border-dashed border-brand-line rounded-lg p-4 mb-5" data-testid="admin-bestsellers-empty">
          No products curated yet. The homepage is currently showing the bundled defaults
          ({defaultIds.length} resolved products). Click <strong>Use current homepage defaults</strong> above
          to seed this list with them, or add products manually from below.
        </div>
      ) : (
        <ul className="space-y-2 mb-6" data-testid="admin-bestsellers-list">
          {ordered.map((p, i) => (
            <li key={p.product_id} className="flex items-center gap-3 bg-brand-cream/40 rounded-lg p-2.5">
              <img src={imageUrl(p.image_url || (p.variants?.[0]?.image_url) || "", { width: 80, height: 80, crop: "fill" })} alt="" className="w-12 h-12 rounded-md object-cover bg-white" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{p.name}</div>
                <div className="text-[11px] text-brand-muted">{p.category} · ₹{p.price}</div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-md hover:bg-white disabled:opacity-30" data-testid={`bestseller-up-${p.product_id}`}><ArrowUp className="w-3.5 h-3.5" strokeWidth={1.7} /></button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === ordered.length - 1} className="p-1.5 rounded-md hover:bg-white disabled:opacity-30" data-testid={`bestseller-down-${p.product_id}`}><ArrowDown className="w-3.5 h-3.5" strokeWidth={1.7} /></button>
                <button type="button" onClick={() => remove(p.product_id)} className="p-1.5 rounded-md hover:bg-white text-brand-chili" data-testid={`bestseller-remove-${p.product_id}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add picker */}
      <div className="border-t border-brand-line pt-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <label className="text-xs text-brand-muted">Add product</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs px-2 py-1.5 rounded-md border border-brand-line bg-white" data-testid="bestseller-category-filter">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-[11px] text-brand-muted">{remaining.length} available</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto" data-testid="bestseller-add-grid">
          {remaining.map((p) => (
            <button key={p.product_id} type="button" onClick={() => add(p.product_id)}
              className="flex items-center gap-2 bg-brand-cream/40 hover:bg-brand-cream rounded-md p-2 text-left transition-colors"
              data-testid={`bestseller-add-${p.product_id}`}>
              <img src={imageUrl(p.image_url || (p.variants?.[0]?.image_url) || "", { width: 80, height: 80, crop: "fill" })} alt="" className="w-9 h-9 rounded object-cover bg-white" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium truncate">{p.name}</div>
                <div className="text-[10px] text-brand-muted truncate">{p.category}</div>
              </div>
              <Plus className="w-3.5 h-3.5 text-brand-chili" strokeWidth={1.8} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
